import { NumberFormatCustomValues } from '@lfg/ui/components';
import { Add as AddIcon, Clear as ClearIcon } from '@mui/icons-material';
import { Box, Paper, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { parse } from 'papaparse';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

import { Show } from 'src/components/Show';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs, ProjectRoyaltyDTO } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { parseConfig, validateCsv } from 'src/utils/template';

import { AdminNumberFormat } from '../AdminNumberFormat';
import { AdminInput } from '../AdminTextField';
import { DownloadButton } from '../DownloadButton';
import { CustomTableBodyCell, CustomTableHeadCell } from '../FundingDistribution/CustomTableCell';
import { StepAccordion } from '../StepAccordion';
import { UploadButton } from '../UploadButton';

export function RoyaltyFee({ isDetail = false }: { isDetail?: boolean }) {
  const { hasDeployed } = useDeployment();

  const {
    control,
    formState: { errors },
  } = useFormContext<ProjectInputs>();
  const { fields, prepend, append, remove, replace } = useFieldArray({
    control,
    name: 'royalties',
  });
  const handleUploadCsvFile = (files: FileList) => {
    const file = files[0];
    if (!file) {
      toast.error('No file selected');
      return;
    }

    const reader = new FileReader();

    reader.onload = async e => {
      try {
        if (!e.target?.result) {
          toast.error('Error reading file');
          return;
        }

        const csv = e.target.result as string;
        const validationRes = validateCsv(csv, {
          headers: ['WALLETADDRESS', 'PERCENTAGE'],
          columnTypes: ['address', 'number'],
        });

        if (!validationRes.isValid) {
          toast.error(validationRes.errors[0]);
          return;
        }

        const parseResult = parse(csv, parseConfig);
        const csvRoyalties = parseResult.data
          .slice(1)
          .filter((row: any) => row.length >= 2 && row[0]?.trim() && !isNaN(Number(row[1])))
          .map((row: any) => {
            const percent = Number(parseFloat(row[1]).toFixed(2)); // Ensure 2 decimal places
            return {
              address: row[0].trim(),
              percent,
            };
          })
          .filter(
            royalty => royalty.address && royalty.percent > 0 && royalty.percent <= 10, // Maximum 10% as per requirements
          );

        if (csvRoyalties.length === 0) {
          toast.error('No valid royalty data found in CSV. Ensure percentages are between 0-10%');
          return;
        }

        // Get current royalties
        const currentRoyalties = [...fields];

        // Create new array for updated existing royalties
        const updatedRoyalties = [...currentRoyalties];

        // Find new royalties that don't exist in current list
        const newRoyalties = csvRoyalties.filter(
          csvRoyalty =>
            !currentRoyalties.some(existing => existing.address.toLowerCase() === csvRoyalty.address.toLowerCase()),
        );

        // Update existing royalties
        let updatedCount = 0;
        csvRoyalties.forEach(csvRoyalty => {
          const existingIndex = currentRoyalties.findIndex(
            existing => existing.address.toLowerCase() === csvRoyalty.address.toLowerCase(),
          );

          if (existingIndex >= 0) {
            // Update the existing royalty percentage
            updatedRoyalties[existingIndex] = {
              ...updatedRoyalties[existingIndex], // Preserve the id and other properties
              percent: csvRoyalty.percent,
            };
            updatedCount++;
          }
        });

        // Calculate total percentage including both updated and new royalties
        const totalPercentage = [...updatedRoyalties, ...newRoyalties].reduce((sum, item) => sum + item.percent, 0);

        if (totalPercentage > 10) {
          toast.error('Total royalty percentage cannot exceed 10%');
          return;
        }

        // First update existing royalties
        replace(updatedRoyalties);

        // Then add new royalties
        if (newRoyalties.length > 0) {
          prepend(newRoyalties as ProjectRoyaltyDTO[]);
        }

        toast.success(`Royalty data updated successfully (${newRoyalties.length} added, ${updatedCount} updated)`);
      } catch (error) {
        console.error('Error processing CSV:', error);
        toast.error('Error processing CSV file');
      }
    };

    reader.onerror = () => {
      toast.error('Error reading the CSV file');
    };

    reader.readAsText(file);
  };

  return (
    <StepAccordion isError={!!errors.royalties} index={7} title='Royalty Fees' open={false}>
      <Typography variant='body1' fontSize={'14px'} fontWeight={400} sx={{ color: 'white' }} paddingBottom={3}>
        Set the royalty percentage distributed to each wallet when a core NFT is resold on marketplaces like OpenSea.
        Visit the{' '}
        <a
          href={'https://link.lfg.inc/kb-coresale-royaltyfee'}
          target={'_blank'}
          style={{ color: 'white', textDecoration: 'underline' }}>
          NFT Royalty Configuration
        </a>{' '}
        article to learn more.
      </Typography>
      <Show when={!hasDeployed && !isDetail}>
        <Stack
          spacing={2}
          gap={2}
          flexDirection={{ xs: 'column', sm: 'row' }}
          marginBottom={3}
          style={{
            width: 'fit-content',
          }}>
          <UploadButton accept='.csv' label='Import CSV File' multiple={false} uploadCallback={handleUploadCsvFile} />
          <DownloadButton label='Download Template' fileUrl='/resources/files/templates/royalty-fee-template.csv' />
        </Stack>
      </Show>
      <TableContainer
        component={Paper}
        sx={{ minWidth: 650, backgroundColor: 'transparent !important', color: colors.neutral90 }}>
        <Table
          sx={{ minWidth: 650, backgroundColor: colors.neutral06, color: colors.neutral90 }}
          size='small'
          aria-label='a dense table'>
          <TableHead sx={{ minWidth: 650, backgroundColor: colors.neutral06, color: colors.neutral90, paddingY: 1 }}>
            <TableRow>
              <CustomTableHeadCell width={'55%'}>Wallet Address</CustomTableHeadCell>
              <CustomTableHeadCell width={'45%'}>Percentage</CustomTableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              background: 'transparent',
              color: 'white',
            }}>
            {fields.map((field, index) => (
              <TableRow
                key={field.id}
                sx={{
                  paddingY: 1,
                  borderBottom: `1px solid ${index === fields.length - 1 ? 'none' : colors.neutral015}`,
                  '&:last-child td, &:last-child th': { border: 0 },
                }}>
                <CustomTableBodyCell>
                  <Controller
                    name={`royalties.${index}.address` as any}
                    control={control}
                    render={({ field }) => (
                      <AdminInput
                        isError={!!errors?.royalties?.[index]?.address}
                        errorMsg={errors?.royalties?.[index]?.address?.message}
                        placeholder={'Enter Wallet Address'}
                        focusedBorderColor='white'
                        readOnly={isDetail || hasDeployed}
                        borderColor={
                          errors?.royalties?.[index]?.address ? colors.error : !field.value ? 'white' : 'transparent'
                        }
                        style={{
                          width: '-webkit-fill-available',
                        }}
                        {...field}
                      />
                    )}
                  />
                </CustomTableBodyCell>
                <CustomTableBodyCell>
                  <div className='flex justify-between items-start'>
                    <Box width={'-webkit-fill-available'}>
                      <Controller
                        name={`royalties.${index}.percent`}
                        control={control}
                        render={({ field }) => (
                          <AdminNumberFormat
                            isAllowed={(values: NumberFormatCustomValues) =>
                              !values.floatValue || (values.floatValue > 0 && values.floatValue <= 10)
                            }
                            onValueChange={values => {
                              field.onChange(Number(values.floatValue));
                            }}
                            decimalScale={2}
                            isError={!!errors?.royalties?.[index]?.percent}
                            errorMsg={errors?.royalties?.[index]?.percent?.message}
                            placeholder={'0%'}
                            focusedBorderColor='white'
                            borderColor={
                              errors?.royalties?.[index]?.percent
                                ? colors.error
                                : !field.value
                                  ? 'white'
                                  : 'transparent'
                            }
                            readOnly={isDetail || hasDeployed}
                            {...field}
                          />
                        )}
                      />
                    </Box>
                    <Stack direction='row' spacing={1} alignItems={'center'} marginLeft={'10px'} marginTop={'12px'}>
                      <Show when={!hasDeployed && !isDetail}>
                        {index === fields.length - 1 && (
                          <AddIcon onClick={() => append({ percent: 0, address: '' })} className='cursor-pointer' />
                        )}
                      </Show>
                      {fields.length > 1 && (
                        <ClearIcon
                          onClick={() => {
                            if (isDetail) return false;
                            remove(index);
                          }}
                          className='cursor-pointer'
                          sx={{
                            color: colors.error,
                          }}
                        />
                      )}
                    </Stack>
                  </div>
                </CustomTableBodyCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ul className='ml-[-28px] flex gap-x-8 items-center text-sm text-[#9C9BA3]'>
        <li>Supported percentage format: 2 decimal places (e.g., 0.01%)</li>
        <li>Maximum fee: 10%</li>
      </ul>
    </StepAccordion>
  );
}
